import { FC, Fragment, useState } from 'react';
import { connect } from 'react-redux';
import { AnyAction, Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import * as vehiclesActions from 'features/vehicles/vehiclesActions';
import * as modalWindowActions from 'root/modal/modalWindowActions';
import * as Sentry from '@sentry/react';
import { DeviceMode, ReduxState, Status, Vehicle } from 'types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { getAllowedDeviceModes, getDeviceModeDisplayName } from 'components/vehicles/ParkingModeDropdown';
import { formatMessage } from 'i18n/CurrentLocale';
import { usePrevious } from 'myParkdUtils/customHooks';
import LoadingSpinner from 'components/LoadingSpinner';


export const getDeviceModeExplanation = (deviceMode: DeviceMode): string => {
    switch (deviceMode) {
        case 'MANUAL': return formatMessage({ id: 'PARKING_MODE_MANUAL_EXPLAINED' });
        case 'MANUAL_SHARED': return formatMessage({ id: 'PARKING_MODE_MANUAL_SHARED_EXPLAINED' });
        case 'SEMI_AUTOMATIC': return formatMessage({ id: 'PARKING_MODE_SEMI_AUTOMATIC_EXPLAINED' });
        case 'AUTOMATIC': return formatMessage({ id: 'PARKING_MODE_AUTOMATIC_EXPLAINED' });
        case 'AUTONOMOUS': return formatMessage({ id: 'PARKING_MODE_AUTONOMOUS_EXPLAINED' });
        case 'SHARED': return formatMessage({ id: 'PARKING_MODE_SHARED_EXPLAINED' });
        default:
            console.error(`getDeviceModeExplanation() - Unhandled deviceMode: ${deviceMode}`);
            Sentry.captureMessage(`getDeviceModeExplanation() - unhandled deviceMode: ${deviceMode}`);
            return 'unknown'
    }
}

export const getChangeDeviceModeErrorMessage = (error: any): string => {
    const errorKey = error?.response?.body?.errorKey;

    if (error.status === 400 && errorKey === 'UNSUPPORTED_MODE_FAILURE') {
        return formatMessage({ id: 'UPDATE_DEVICE_MODE_API_ERROR_UNSUPPORTED_MODE_FAILURE' })
    } else if (error.status === 400 && errorKey === 'DEVICE_CLAIMED_FAILURE') {
        return formatMessage({ id: 'UPDATE_DEVICE_MODE_API_ERROR_DEVICE_CLAIMED_FAILURE' })
    } else if (error.status === 401 && errorKey === 'UNAUTHORIZED') {
        return formatMessage({ id: 'UPDATE_DEVICE_MODE_API_ERROR_UNAUTHORIZED' })
    } else if (error.status === 404 && 'UNKNOWN_DEVICE') {
        return formatMessage({ id: 'UPDATE_DEVICE_MODE_API_ERROR_UNKNOWN_DEVICE' })
    } else if (error.status === 409 && 'DEVICE_ACTIVE_SESSION') {
        return formatMessage({ id: 'UPDATE_DEVICE_MODE_API_ERROR_DEVICE_ACTIVE_SESSION' })
    } else {
        console.error('getChangeDeviceModeErrorMessage() - unhandled error', error);
        Sentry.captureMessage(`getChangeDeviceModeErrorMessage() - unhandled error: ${error.status}`);
        return formatMessage({ id: 'UPDATE_DEVICE_MODE_API_ERROR_UNKNOWN_ERROR' });
    }
}

export type ResponseMessageProps = {
    deviceUpdateStatus: Status,
    licensePlate: string,
    deviceMode: DeviceMode,
    closeModal: Function,
}

export const ResponseMessage: FC<ResponseMessageProps> = ({ deviceUpdateStatus, licensePlate, deviceMode, closeModal }) => {
    return (
        <div>
            {deviceUpdateStatus.loading && (
                <LoadingSpinner />
            )}
            {deviceUpdateStatus.succeeded && (
                <div data-testid='response-message-succeeded'>
                    <FontAwesomeIcon icon={faCheckCircle} size='lg' style={{ marginRight: '0.5em', color: '#2fbb7c' }} />
                    <FormattedMessage id='UPDATE_DEVICE_MODE_STATUS_SUCCEEDED'
                        values={{
                            b: chunks => <strong>{chunks}</strong>,
                            licensePlate,
                            deviceMode: getDeviceModeDisplayName(deviceMode)
                        }}
                    />
                </div>
            )}
            {deviceUpdateStatus.error && (
                <div data-testid='response-message-error'>
                    <p className='txt-red'>
                        <FormattedMessage id='UPDATE_DEVICE_MODE_STATUS_ERROR'
                            values={{
                                b: chunks => <strong>{chunks}</strong>,
                                licensePlate: licensePlate
                            }}
                        />
                    </p>
                    <ul className="list-warnings">
                        <li className='red'>
                            <FontAwesomeIcon data-testid="errorkey-icon" icon={faTimesCircle} size='sm' style={{ marginRight: '0.5em' }} />
                            {getChangeDeviceModeErrorMessage(deviceUpdateStatus.error)}
                        </li>
                    </ul>
                </div>
            )}

            <button className='btn-blue' onClick={() => closeModal()}>
                <FormattedMessage id='GLOBAL_CLOSE' />
            </button>
        </div>
    )
}

export const ChangeParkingModeModal = (props: ChangeParkingModeModalProps) => {
    const { deviceUpdateStatus, autonomousAllowed } = props;
    const { modalTitle, vehicle, deviceMode } = props.modalData;
    const prevDeviceUpdateStatus = usePrevious(deviceUpdateStatus);
    const availableDeviceModes = getAllowedDeviceModes(vehicle, autonomousAllowed);

    const [selectedMode, setSelectedMode] = useState<DeviceMode>(deviceMode);

    const updateParkingMode = () => {
        props.updateDeviceMode(vehicle.localizedLicensePlate, selectedMode);
    }

    const actionSucceeded = !!prevDeviceUpdateStatus?.loading && deviceUpdateStatus.succeeded;
    const actionFailed = !!prevDeviceUpdateStatus?.loading && deviceUpdateStatus.error;

    return (
        <div className="panel large radius-rectangle full-width-form confirmation-modal">
            <div className="panel-header">
                <h1 className="bold-title">{modalTitle}</h1>
            </div>
            <div className="panel-content align-left">

                {(deviceUpdateStatus.loading || actionSucceeded || actionFailed)
                    ? <ResponseMessage
                        deviceUpdateStatus={deviceUpdateStatus}
                        closeModal={props.closeModal}
                        licensePlate={vehicle.localizedLicensePlate.licensePlate}
                        deviceMode={selectedMode}
                    />
                    :
                    <Fragment>
                        <div className='flex-2-col-parent'>
                            <div className='flex-2-col'>
                                <select data-testid='select-current-device-mode' disabled>
                                    <option key={vehicle.mode} value={vehicle.mode}>
                                        {getDeviceModeDisplayName(vehicle.mode)}
                                    </option>
                                </select>
                            </div>
                            <div style={{ padding: '5px' }}><FontAwesomeIcon icon={faArrowRight} style={{ paddingLeft: '5px', paddingRight: '5px' }} /></div>
                            <div className='flex-2-col'>
                                <div className='select-wrap'>
                                    <select data-testid='select-new-device-mode' value={selectedMode} onChange={(e) => setSelectedMode(e.target.value as DeviceMode)}>
                                        {availableDeviceModes.map((deviceMode) => (
                                            <option key={deviceMode} value={deviceMode}>{getDeviceModeDisplayName(deviceMode)}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <p className='txt-18-blue' style={{ textAlign: 'left', padding: '20px 5px 0 5px' }}>
                            {getDeviceModeExplanation(selectedMode)}
                        </p>

                        <button className='btn-blue' onClick={updateParkingMode}>
                            <FormattedMessage id='CTA_UPDATE_PARKING_MODE' />
                        </button>
                    </Fragment>
                }
            </div>
        </div>
    );
};


export type ModalProps = {
    modalTitle: string,
    vehicle: Vehicle,
    deviceMode: DeviceMode
}
type OwnProps = {
    modalData: ModalProps
};
type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
export type ChangeParkingModeModalProps = OwnProps & StateProps & DispatchProps;

function mapStateToProps(state: ReduxState) {
    return {
        deviceUpdateStatus: state.companyVehicles.deviceUpdateStatus,
        autonomousAllowed: state.companyDetails?.companyDetails?.autonomousAllowed,
    };
}
function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators({
        updateDeviceMode: vehiclesActions.updateDeviceMode,
        closeModal: modalWindowActions.closeModal,
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ChangeParkingModeModal);